import { Button } from '@app/components';
import { PricingPlans } from '@app/components/PricingPlans';
import Logo from '@app/assets/horizontal-logo.svg?react';
import { useTrialWelcomePage } from './TrialWelcome.hooks.ts';
import { useEffect, useState } from 'react';
import { getUserWithBusinesses } from '@app/services/user';
import { UserInterface } from '@app/models/user';
import { useQuery } from '@tanstack/react-query';
import { getPlans } from '@app/services/plans';
import { WELCOME_FEATURES } from '@app/utils/consts';

export const TrialWelcome = () => {
  const { 
    navigate, 
    handleStartTrial, 
    isCreatingAccount, 
    selectedPlan,
    setSelectedPlan
  } = useTrialWelcomePage();

  const [_, setUser] = useState<UserInterface | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch plans from API
  const { data: plansResponse } = useQuery({
    queryKey: ['get-plans'],
    queryFn: () => getPlans(),
  });

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUserWithBusinesses();
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6">
      <Logo width={139} height={110} className="mb-auto" />
      <div className="bg-white text-center space-y-6 rounded max-w-[1200px] px-10 pt-10 pb-6 mb-auto">
        <h2 className="font-bold text-3xl">Choose Your Plan</h2>
        <p className="text-base-coolGray max-w-2xl mx-auto">Select the perfect plan to continue enjoying powerful review management tools and grow your business reputation.</p>

        <PricingPlans
          selectedPlan={selectedPlan}
          onPlanSelect={setSelectedPlan}
          onPlanTypeSelect={() => {}}
          showTrialOffer={false}
          plans={plansResponse?.data?.plans}
        />

        {/* Features Section */}
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold mb-2 text-brand-baseBlue">What's included in every plan</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {WELCOME_FEATURES.map((feature, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="flex justify-center mb-4">{feature.icon}</div>
              <h4 className="font-semibold text-sm mb-2">{feature.title}</h4>
              <p className="text-xs text-base-coolGray">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center gap-4 w-full">
          <Button
            onClick={handleStartTrial}
            disabled={isCreatingAccount || !selectedPlan}
            loading={isCreatingAccount}
            className="w-full bg-blue-400 hover:bg-blue-500 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Choose Plan
          </Button>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>←</span>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
